import { Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react'
import * as Yup from 'yup';
import { Box, Flex, Button, Heading, Card } from 'rebass/styled-components'
import FullLayout2 from '../components/FullLayout';
import FormField from '../components/FormField';
import { TextInputWithLabel } from '../components/TextInput';
import { gql, useMutation } from '@apollo/client';
import { useNavigate } from 'react-router-dom';
import { ICompleteProfileInput, ICompleteProfileOutput } from '../graphql/types/mutations/login';
import withAnalytics from '../lib/withAnalytics';
import { CURRENT_USER, GET_ANALYTICS_ID } from '../graphql/queries';
import LoadingScreen from '../components/LoadingScreen';
import styled from 'styled-components';
import Header from '../components/Header';
import withSession from '../lib/withSession';
import { IUser } from '../graphql/types/queries/user';
import { useToasts } from 'react-toast-notifications';


export const AvatarImg = styled(Flex)`
  background: #FFE3D8;
  border-radius: 50%;
  border: 0px solid #ccc;
  overflow: hidden;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  min-width: 48px;
  & > img {
    width: 48px;
  }
`;
const schema = Yup.object().shape({
  password: Yup.string()
    .min(6, 'min. length: 6')
    .trim()
    .required(),
  passwordConfirm: Yup
    .string()
    .trim()
    .oneOf([Yup.ref('password'), undefined], 'Passwords must match')
    .required(),
});

const COMPLETE_REGISTRATION = gql`
  mutation completeHealthCoachUserRegistration(
    $password: String!,
    $helpscoutId: Int,
    $calendlyUserId: String,
    $displayName: String,
    $signature: String,
    $initiateConversationMessage: String,
    $mentionTag: String,
    $calendlyUserEmail: String
  ) {
    completeHealthCoachUserRegistration(
      password:$password,
helpscoutId:$helpscoutId,
calendlyUserId:$calendlyUserId,
displayName:$displayName,
signature:$signature,
initiateConversationMessage:$initiateConversationMessage,
mentionTag:$mentionTag,
calendlyUserEmail:$calendlyUserEmail
    ) {
      ok
    }
  }
`;

const Container = styled(Box)`
  max-width: 1024px;
  width: 100%;
  margin: 0 auto !important;
`;

const CardContainer = styled(Flex)`
  max-width: 400px;
`;

interface CompleteProfileProps {
  analytics: any,
  session: IUser
}

const CompleteProfile: React.FC<CompleteProfileProps> = ({ analytics, session }) => {
  const { addToast } = useToasts()
  const navigate = useNavigate()
  const [isLoading, setIsLoading] = useState(false);
  const [isExpanded, setisExpanded] = useState(false);
  const [initialValues, setInitialValues] = useState({
    passwordConfirm: '',
    password: '',
    helpscoutId: session.helpscoutId,
    calendlyUserId: session.calendlyUserId,
    displayName: session.displayName,
    signature: session.signature,
    initiateConversationMessage: session.initiateConversationMessage,
    mentionTag: session.mentionTag,
    calendlyUserEmail: session.calendlyUserEmail,
  })
  const [loginMutate, { client }] = useMutation<ICompleteProfileOutput, ICompleteProfileInput>(COMPLETE_REGISTRATION, {
    refetchQueries: [{ query: CURRENT_USER }],
    awaitRefetchQueries: true,
    onError: (e) => {
      e.graphQLErrors.forEach((error) => {
        addToast(error.message, {
          appearance: 'error',
          autoDismiss: true,
        }, () => {
          setIsLoading(false)
          setisExpanded(false)
        });
      });
    }
  })

  useEffect(() => {
    analytics.page();
    localStorage.removeItem('initialToken');
  }, [])

  const handleSubmit = async (values: any, actions: any) => {
    if (!isExpanded) {
      setisExpanded(true)
      return
    }
    setInitialValues({...values})
    setIsLoading(true);
    try {
      const { data, errors } = await loginMutate({ variables: { ...values } });

      if (!data?.completeHealthCoachUserRegistration || !data?.completeHealthCoachUserRegistration.ok) {
        if (errors) {
          setisExpanded(false)
          errors.forEach((error) => {
            addToast(error.message, {
              appearance: 'error',
              autoDismiss: true,
            });
          });
        }
        setIsLoading(false);
        return
      }

      const aa = await client.query({
        query: GET_ANALYTICS_ID,
        fetchPolicy: 'network-only',
      });

      if (aa && aa.data && aa.data.getAnalyticsId) {
        analytics.Alias(aa.data.getAnalyticsId);

        try {
          analytics.Identify(aa.data.getAnalyticsId, {
            isProvider: true,
          });
        } catch (error) {
        }
      }

      navigate('/');
    } catch (error: any) {
      setIsLoading(false);
      setisExpanded(false)
      if (!error.graphQLErrors) {
        return;
      }
    }
  };

  if (isLoading) {
    return <LoadingScreen loading={true} />;
  }

  return (
    <FullLayout2 style={{ overflow: 'auto', zIndex: 10 }}>
      <Container>
        <Flex flex={1} flexDirection="column">
          <Header dark={false} childContainerStyle={{ marginTop: '-16px' }}>

            <Heading sx={{ marginLeft: '97px' }}>Health Coaches</Heading>
          </Header>
        </Flex>
        <Flex flex={1} alignItems="center" justifyContent="center">
          <CardContainer flex={1} px={3}>
            <Card p={4}>
              <Flex flexDirection="column">
                <Box mb={4}>
                  <Heading textAlign="center" mb={3}>
                    Hi {session.displayName}
                  </Heading>
                  <Heading textAlign="center" mb={3} fontSize={'18px'}>
                    Let's finish your onboarding!
                  </Heading>
                  <Flex justifyContent={'center'} alignItems={'center'}>
                    <AvatarImg style={{ alignSelf: 'center' }}>
                      <img
                        alt=""
                        src={session.avatar}
                      />
                    </AvatarImg>
                  </Flex>
                </Box>
                <Box>
                  <Formik
                    initialValues={initialValues}
                    isInitialValid={() => {
                      try {
                        const aa = schema.validateSync({
                          passwordConfirm: '',
                          password: '',
                        });

                        if (aa.password || aa.passwordConfirm) {
                          return false;
                        }
                        return true;
                      } catch (err) {
                        return false;
                      }
                    }}
                    validationSchema={schema}
                    onSubmit={(values, actions) => handleSubmit(values, actions)}>
                    {({ touched, errors, isValid, values }) => {
                      return (
                        <>
                          <Form>
                            {!isExpanded ? (
                              <>
                                <FormField mb={4} error={touched.password && errors.password}>
                                  <TextInputWithLabel type="password" placeholder="Password *" name="password" id="input_password" />
                                </FormField>

                                <FormField mb={4} error={touched.passwordConfirm && errors.passwordConfirm}>
                                  <TextInputWithLabel placeholder="Confirm Password *" type="password" name="passwordConfirm" id="input_passwordConfirm" />
                                </FormField>

                                <Flex alignItems="center" flexDirection="column">
                                  <Button disabled={(!isValid && !!errors && Object.keys(errors).length > 0) || isLoading} variant="primary" width={1 / 2} type="submit" id="btn_Expand">
                                    Continue
                                  </Button>
                                </Flex>
                              </>
                            ) : (
                              <>
                                <FormField mb={4} error={touched.helpscoutId && errors.helpscoutId}>
                                  <TextInputWithLabel placeholder="Helpscout ID" disabled={!!session.helpscoutId} type="numbers" name="helpscoutId" id="input_helpscoutId" />
                                </FormField>

                                <FormField mb={4} error={touched.calendlyUserId && errors.calendlyUserId}>
                                  <TextInputWithLabel placeholder="Calendly ID" disabled={!!session.calendlyUserId} name="calendlyUserId" id="input_calendlyUserId" />
                                </FormField>

                                <FormField mb={4} error={touched.calendlyUserEmail && errors.calendlyUserEmail}>
                                  <TextInputWithLabel autoComplete="off" data-lpignore="true" placeholder="calendly User Email" disabled={!!session.calendlyUserEmail} name="calendlyUserEmail" id="input_calendlyUserEmail" />
                                </FormField>

                                <FormField mb={4} error={touched.displayName && errors.displayName}>
                                  <TextInputWithLabel placeholder="Display name" disabled={!!session.displayName} name="displayName" id="input_displayName" />
                                </FormField>

                                <FormField mb={4} error={touched.mentionTag && errors.mentionTag}>
                                  <TextInputWithLabel placeholder="mention tag" name="mentionTag" id="input_mentionTag" />
                                </FormField>

                                <Flex alignItems="center" flexDirection="column">
                                  <Button disabled={(!isValid && !!errors && Object.keys(errors).length > 0) || isLoading} variant="primary" width={1 / 2} type="submit" id="btn_login">
                                    Finish
                                  </Button>
                                </Flex>
                              </>
                            )

                            }
                          </Form>
                        </>
                      );
                    }}
                  </Formik>
                </Box>
              </Flex>
            </Card>
          </CardContainer>
        </Flex>
      </Container>
    </FullLayout2>
  );
}

export default withAnalytics(withSession(CompleteProfile))